import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { companyAPI } from '../api/client';
import useTenantBranding from '../hooks/useTenantBranding';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Loader2, Mail, Lock, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { branding } = useTenantBranding();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.company_name) return 'Company name is required';
    if (!form.first_name || !form.last_name) return 'Your name is required';
    if (!form.email) return 'Email is required';
    if (!form.phone) return 'Phone is required';
    if (form.password.length < 6) return 'Password must be at least 6 characters';
    if (form.password !== form.confirm_password) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setLoading(true);
    try {
      // 1. Create the company — reuse the admin's email & phone as the
      //    company contact (admin can edit later in Settings).
      const company = await companyAPI.create({
        name: form.company_name,
        email: form.email,
        phone: form.phone,
      });

      // 2. Register the admin user under that company (auto-login)
      const result = await register({
        company_id: company.id,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        role: 'company_admin',
      });

      if (!result.success) {
        toast.error(result.error || 'Registration failed');
        return;
      }
      toast.success(`Welcome to My Key Boss, ${form.first_name}!`);
      navigate('/');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Registration failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md" data-testid="register-page">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto w-20 h-20 rounded-2xl overflow-hidden bg-primary/5 flex items-center justify-center p-1">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="w-full h-full object-contain" />
            ) : (
              <img src="/brand/logo.png" alt="My Key Boss" className="w-full h-full object-contain" />
            )}
          </div>
          <div>
            <CardTitle className="text-3xl font-bold">
              {branding ? `Join ${branding.name}` : 'Create Your Account'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Start your 14-day free trial — no credit card required.
            </CardDescription>
            {branding && (
              <p className="text-[10px] text-muted-foreground mt-1">powered by My Key Boss</p>
            )}
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                placeholder="ACME Locksmith LLC"
                value={form.company_name}
                onChange={(e) => update('company_name', e.target.value)}
                disabled={loading}
                data-testid="reg-company-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  placeholder="John"
                  value={form.first_name}
                  onChange={(e) => update('first_name', e.target.value)}
                  disabled={loading}
                  data-testid="reg-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  placeholder="Smith"
                  value={form.last_name}
                  onChange={(e) => update('last_name', e.target.value)}
                  disabled={loading}
                  data-testid="reg-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  className="pl-10"
                  placeholder="you@yourcompany.com"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  disabled={loading}
                  data-testid="reg-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Phone *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-10"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  disabled={loading}
                  data-testid="reg-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  placeholder="At least 6 characters"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  disabled={loading}
                  data-testid="reg-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  placeholder="Re-enter password"
                  value={form.confirm_password}
                  onChange={(e) => update('confirm_password', e.target.value)}
                  disabled={loading}
                  data-testid="reg-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-gradient-primary"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...
                </>
              ) : (
                'Create Account & Start Free Trial'
              )}
            </Button>
          </CardContent>
        </form>

        <CardFooter>
          <div className="text-sm text-center text-muted-foreground w-full">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in here
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
